import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuCategory } from '@/mocks/menuData';

interface MenuCategoryNavProps {
  categories: MenuCategory[];
}

export default function MenuCategoryNav({ categories }: MenuCategoryNavProps) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(categories[0]?.id || '');

  // Re-render when language changes
  const currentLang = i18n.language;

  useEffect(() => {
    const handleScroll = () => {
      for (let i = categories.length - 1; i >= 0; i--) {
        const el = document.getElementById(`cat-${categories[i].id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            setActive(categories[i].id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories]);

  const scrollToCategory = (id: string) => {
    setActive(id);
    const el = document.getElementById(`cat-${id}`);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-16 md:top-20 z-30 bg-black/95 backdrop-blur-md border-b border-white/5 py-3">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" key={currentLang}>
          {categories.map((cat) => (
            <button
              key={`${cat.id}-${currentLang}`}
              onClick={() => scrollToCategory(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all duration-200 cursor-pointer border ${
                active === cat.id
                  ? 'bg-[#F57F25] text-black border-[#F57F25]'
                  : 'bg-transparent text-white/60 border-white/10 hover:border-[#F57F25]/40 hover:text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon || 'ri-restaurant-line'} text-xs`}></i>
              </div>
              {cat.labelKey && t(cat.labelKey) !== cat.labelKey ? t(cat.labelKey) : (cat.label || cat.name)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
